import { authStorageInitialState, getContrastColor, RoleType } from "@mc-intranet/shared";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import BugReportIcon from "@mui/icons-material/BugReport";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { QueryCache } from "@tanstack/react-query";
import { useAtom } from "jotai";
import React, { useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import logoDark from "../../../../assets/logo-dark.svg";

import logo from "../../../../assets/logo.svg";
import { authStorageAtom, originalRoleAtom } from "../../../../atoms";
import { ColorModeContext, darkMainColor } from "../../../../theme";
import { RewardCoinsChip } from "../../RewardCoinsChip"; // eslint-disable-next-line @typescript-eslint/no-empty-interface

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props {}

// const pages = [
//   "home",
//   "oggi",
//   "presenze",
//   "ferie",
//   "permessi",
//   "stats",
//   "notifiche"
// ];

export const Nav: React.FC<Props> = (props) => {
  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);

  const [authStorage, setAuthStorage] = useAtom(authStorageAtom);
  const [originalRole, setOriginalRole] = useAtom(originalRoleAtom);
  const { user, isLoggedIn, role } = authStorage;
  const userAvatar = `${user?.firstName?.charAt(0) || ""}${
    user?.lastName?.charAt(0) || ""
  }`;

  const isDevEnv = process.env["NX_DEV_ENV"] === "true";
  const isMasquerading = !!originalRole;

  const handleMasqueradeAsAdmin = () => {
    setOriginalRole(role ?? null);
    setAuthStorage((prev) => ({ ...prev, role: RoleType.admin }));
    handleCloseUserMenu();
  };

  const handleRestoreRole = () => {
    setAuthStorage((prev) => ({ ...prev, role: originalRole }));
    setOriginalRole(null);
    handleCloseUserMenu();
  };

  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  // console.log("location :>> ", location);
  const mode = theme.palette.mode;
  // console.log("theme.palette.mode :>> ", mode);
  console.log("user: >>", user);

  // const logo =
  //   mode === "light"
  //     ? require("../../assets/logo-dark.svg")
  //     : require("../../assets/logo.svg");

  const canSeeOtherUsers =
    role === RoleType.account ||
    role === RoleType.manager ||
    role === RoleType.admin;

  // const _pages = canSeeOtherUsers
  //   ? pages
  //   : pages.filter((page) => page !== "stats");

  const pages = useMemo(
    () => [
      {
        title: "home",
        url: "/home",
        private: false
      },
      {
        title: "oggi",
        url: "/oggi",
        private: false
      },
      {
        title: "presenze",
        url: "/presenze",
        private: false
      },
      {
        title: "ferie",
        url: "/ferie",
        private: false
      },
      {
        title: "permessi",
        url: "/permessi",
        private: false
      },
      // {
      //   title: "timbrature",
      //   url: "/timbrature",
      //   private: role !== RoleType.admin
      // },
      {
        title: "approvazione",
        url: "/approvazione",
        private: role !== RoleType.admin
      },
      {
        title: "stats",
        url: "/stats",
        private: false
      },
      {
        title: "notifiche",
        url: "/notifiche",
        private: role !== RoleType.admin
      }
    ],
    [role]
  );

  // const _pages = pages;

  const logout = () => {
    setAuthStorage(authStorageInitialState);
    const queryCache = new QueryCache({
      onError: (error: any) => {
        console.log(error);
      },
      onSuccess: (data: any) => {
        console.log(data);
      }
    });
    queryCache.clear();
  };

  const handleOpenNavMenu = (event: any) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: any) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    handleCloseUserMenu();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: "background.default"
      }}
      elevation={theme.palette.mode === "dark" ? 1 : 5}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <RouterLink to="/">
            <Box
              component="img"
              className="logo"
              src={mode === "dark" ? logo : logoDark}
              alt="Mediacross logo"
              maxWidth="150px"
              marginRight={4}
              display={{ xs: "none", md: "flex" }}
            />
          </RouterLink>
          {/* mobile  */}

          <>
            <Box
              color="text.primary"
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block", md: "none" }
                }}
              >
                {isLoggedIn &&
                  pages
                    .filter((p) => !p.private)
                    .map((page) => (
                      <MenuItem key={page.title} onClick={handleCloseNavMenu}>
                        <Button
                          component={RouterLink}
                          to={`/${page}`}
                          size="small"
                          sx={{
                            color:
                              location.pathname === `${page.url}`
                                ? "primary.main"
                                : "secondary.main",
                            textDecoration: "none",
                            justifyContent: "flex-start",
                            px: 0
                            // textTransform: "capitalize"
                          }}
                        >
                          <Typography textAlign="center">
                            {page.title}
                          </Typography>
                        </Button>
                      </MenuItem>
                    ))}
                <MenuItem onClick={handleCloseNavMenu}>
                  <Button
                    component={RouterLink}
                    to={`/app-mobile`}
                    size="small"
                    sx={{
                      color:
                        location.pathname === `/app-mobile`
                          ? "primary.main"
                          : "secondary.main",
                      textDecoration: "none",
                      justifyContent: "flex-start",
                      px: 0
                      // textTransform: "capitalize"
                    }}
                  >
                    <Typography textAlign="center">App Mobile</Typography>
                  </Button>
                </MenuItem>
              </Menu>
            </Box>
            {/* desktop */}
            <Box
              sx={{ flexGrow: 1 }}
              display={{ xs: "flex", md: "none" }}
              justifyContent="center"
            >
              <RouterLink to="/">
                <Box
                  component="img"
                  className="logo-mobile"
                  src={mode === "dark" ? logo : logoDark}
                  alt="Mediacross logo"
                  maxWidth="150px"
                />
              </RouterLink>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
              {isLoggedIn &&
                pages
                  .filter((p) => !p.private)
                  .map((page) => (
                    <Button
                      component={RouterLink}
                      key={page.title}
                      to={`${page.url}`}
                      onClick={handleCloseNavMenu}
                      sx={{
                        my: 2,
                        color:
                          location.pathname === `${page.url}`
                            ? "primary.main"
                            : "text.primary",
                        textDecoration: "none"
                        // textTransform: "capitalize"
                      }}
                    >
                      {page.title}
                    </Button>
                  ))}
              <Button
                component={RouterLink}
                to={`/app-mobile`}
                size="small"
                sx={{
                  my: 2,
                  color:
                    location.pathname === `/app-mobile`
                      ? "primary.main"
                      : "secondary.main",
                  textDecoration: "none"
                }}
                onClick={handleCloseNavMenu}
              >
                App Mobile
              </Button>
            </Box>
            {isLoggedIn ? (
              <Box
                sx={{
                  flexGrow: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                {user?.rewardCoins !== null &&
                  user?.rewardCoins !== undefined && (
                    <RewardCoinsChip
                      coins={user.rewardCoins}
                      punchShiftLimit={
                        user.punchShiftLimitMinutes ?? 15
                      }
                    />
                  )}
                <Tooltip title="Apri impostazioni">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar
                      sx={{
                        bgcolor: user?.color,
                        color: getContrastColor(
                          user?.color as string | undefined,
                          128,
                          "#fff",
                          darkMainColor
                        )
                      }}
                    >
                      {userAvatar}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem onClick={handleLogout}>
                    <Typography textAlign="center">Logout</Typography>
                  </MenuItem>
                  <MenuItem onClick={colorMode.toggleColorMode}>
                    <Typography sx={{ mr: 1 }}>
                      {theme.palette.mode === "dark" ? "light" : "dark"} mode
                    </Typography>
                    {theme.palette.mode === "dark" ? (
                      <Brightness7Icon />
                    ) : (
                      <Brightness4Icon />
                    )}
                  </MenuItem>
                  {isDevEnv && (
                    <>
                      <Divider />
                      {isMasquerading ? (
                        <MenuItem onClick={handleRestoreRole}>
                          <BugReportIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "warning.main" }}
                          />
                          <Box>
                            <Typography
                              variant="body2"
                              color="warning.main"
                              fontWeight={600}
                            >
                              Smaschera
                            </Typography>
                            <Chip
                              label={originalRole}
                              size="small"
                              sx={{ mt: 0.3, height: 18, fontSize: "0.65rem" }}
                            />
                          </Box>
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={handleMasqueradeAsAdmin}>
                          <BugReportIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "info.main" }}
                          />
                          <Typography variant="body2" color="info.main">
                            Passa ad Admin
                          </Typography>
                        </MenuItem>
                      )}
                    </>
                  )}
                </Menu>
              </Box>
            ) : (
              <Box height="40px" width="40px" />
            )}
          </>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Nav;
